import React, { useState } from 'react';
import { toast } from 'sonner';
import { adminAPI } from '../../lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogBody, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';

// Admin score/winner correction for a completed match, launched from the
// Challenges list. Reuses the authoritative backend correction endpoint
// (/admin/matches/:id/correct) which reconciles the ladder or safely blocks.
export default function EditScoreDialog({ open, onClose, challenge, onSaved }) {
  const m = challenge?.match;
  const [score, setScore] = useState(m?.score || '');
  const [winnerId, setWinnerId] = useState(m?.winner_id || challenge?.challenger_id);
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);

  React.useEffect(() => {
    if (open) {
      setScore(challenge?.match?.score || '');
      setWinnerId(challenge?.match?.winner_id || challenge?.challenger_id);
      setConfirming(false);
    }
  }, [open, challenge]);

  if (!challenge || !m) return null;

  const winnerChanged = winnerId !== m.winner_id;
  const scoreChanged = (score || '') !== (m.score || '');
  const dirty = winnerChanged || scoreChanged;

  const save = async () => {
    setBusy(true);
    try {
      const res = await adminAPI.correctMatch(m.match_id, { score: score.trim(), winner_id: winnerId });
      toast.success(res.data?.ladder_changed
        ? 'Score corrected — ladder updated.'
        : 'Score corrected.');
      onSaved?.();
      onClose?.();
    } catch (err) {
      // 409 = cannot safely recalculate; surfaced verbatim so the admin knows why.
      toast.error(err.response?.data?.detail || 'Could not save correction');
      setConfirming(false);
    } finally {
      setBusy(false);
    }
  };

  const WinnerBtn = ({ id, name }) => (
    <button
      type="button"
      data-testid={`edit-score-winner-${id === challenge.challenger_id ? 'challenger' : 'challenged'}`}
      onClick={() => setWinnerId(id)}
      className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium border transition-all ${
        winnerId === id
          ? 'bg-[#015e3f] text-white border-[#015e3f]'
          : 'bg-white text-[#013523] border-[#e3dcd1] hover:border-[#015e3f]/40'
      }`}
    >
      {name}
    </button>
  );

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <DialogContent data-testid="edit-score-dialog">
        <DialogHeader>
          <DialogTitle>{confirming ? 'Confirm correction' : 'Edit match score'}</DialogTitle>
        </DialogHeader>

        {!confirming ? (
          <DialogBody className="space-y-4">
            <div className="text-sm text-[#6a766e]">
              {challenge.challenger_name} vs {challenge.challenged_name}
              <div className="text-xs mt-1">
                Current: <span className="font-mono">{m.score || '—'}</span> → winner{' '}
                <span className="font-medium text-[#013523]">
                  {m.winner_id === challenge.challenger_id ? challenge.challenger_name : challenge.challenged_name}
                </span>
              </div>
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wider text-[#6a766e]">Score</label>
              <input
                data-testid="edit-score-input"
                value={score}
                onChange={(e) => setScore(e.target.value)}
                placeholder="e.g. 6-4, 6-3"
                className="mt-1 w-full bg-white border border-[#e3dcd1] rounded-xl px-4 py-2.5 text-sm text-[#013523] focus:outline-none focus:ring-2 focus:ring-[#015e3f]/20 focus:border-[#015e3f]"
              />
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wider text-[#6a766e]">Winner</label>
              <div className="mt-1 flex gap-2">
                <WinnerBtn id={challenge.challenger_id} name={challenge.challenger_name} />
                <WinnerBtn id={challenge.challenged_id} name={challenge.challenged_name} />
              </div>
              {winnerChanged && (
                <p className="text-[11px] text-[#8a6d2c] mt-2">
                  Changing the winner may adjust ladder positions. If it can't be recalculated safely, the change will be blocked with an explanation.
                </p>
              )}
            </div>
          </DialogBody>
        ) : (
          <DialogBody className="space-y-3 text-sm text-[#013523]">
            <p>Apply this correction?</p>
            <div className="bg-[#fbfaf8] border border-[#e3dcd1] rounded-xl p-3 space-y-1 text-xs">
              <div>Score: <span className="font-mono">{m.score || '—'}</span> → <span className="font-mono font-semibold">{score || '—'}</span></div>
              <div>Winner: <span className="font-medium">{m.winner_id === challenge.challenger_id ? challenge.challenger_name : challenge.challenged_name}</span> → <span className="font-semibold">{winnerId === challenge.challenger_id ? challenge.challenger_name : challenge.challenged_name}</span></div>
            </div>
            <p className="text-[11px] text-[#6a766e]">This is recorded in the audit log.</p>
          </DialogBody>
        )}

        <DialogFooter>
          {!confirming ? (
            <>
              <Button variant="outline" onClick={onClose} data-testid="edit-score-cancel">Cancel</Button>
              <Button
                data-testid="edit-score-next"
                disabled={!dirty || !score.trim()}
                onClick={() => setConfirming(true)}
                className="bg-[#015e3f] hover:bg-[#014d34] text-white"
              >
                Review
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" onClick={() => setConfirming(false)} disabled={busy} data-testid="edit-score-back">Back</Button>
              <Button data-testid="edit-score-confirm" onClick={save} disabled={busy} className="bg-[#015e3f] hover:bg-[#014d34] text-white">
                {busy ? 'Saving…' : 'Confirm correction'}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
